/* eslint-disable react/require-default-props */
/* eslint-disable react/no-unused-prop-types */
import React, { useState, useMemo, useCallback } from 'react';
import { Brushaway, BrushawayHeader, BrushawayBody, BrushawayFooter } from '@ui/molecules/Brushaway';
import { Button } from '@ui';
import { FilterBar } from '@ui/organisms/FilterBar';
import { Table, TableSize } from '@ui/molecules/Table';
import { PhotoCarousel } from '@ui/molecules/PhotoCarousel';
import { getCurrentLanguage, translate } from '@utils/i18n';
const NS = 'features.proposals.attachments_step';
const t = (key, params = {}) => translate(key, Object.assign({ namespace: NS }, params));
// Same expanded panel as AddOpenDeficienciesBrushaway — photo + checkbox columns need the room.
export const BUILDING_PHOTOS_BRUSHAWAY_MIN_WIDTH = 960;
// ── Date helpers ──────────────────────────────────────────────────────────────
function parseDateSafe(raw) {
    const d = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? new Date(`${raw}T00:00:00`) : new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
}
function dateLocale() {
    const lang = getCurrentLanguage();
    if (lang === 'es')
        return 'es';
    if (lang === 'fr')
        return 'fr';
    return 'en-US';
}
export function formatDateTaken(dateTaken) {
    if (!dateTaken)
        return '';
    const d = parseDateSafe(dateTaken);
    if (!d)
        return '';
    return d.toLocaleDateString(dateLocale(), { year: 'numeric', month: '2-digit', day: '2-digit' });
}
function monthGroupTitle(dateTaken) {
    if (!dateTaken)
        return null;
    const d = parseDateSafe(dateTaken);
    if (!d)
        return null;
    return d.toLocaleDateString(dateLocale(), { year: 'numeric', month: 'long' });
}
function monthSortKey(dateTaken) {
    if (!dateTaken)
        return '9999-99';
    const d = parseDateSafe(dateTaken);
    if (!d)
        return '9999-99';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
}
export function groupPhotosByMonth(photos) {
    const noDateLabel = t('no_date_group');
    const groupMap = photos.reduce((acc, photo) => {
        var _a;
        const title = (_a = monthGroupTitle(photo.dateTaken)) !== null && _a !== void 0 ? _a : noDateLabel;
        const sortKey = monthSortKey(photo.dateTaken);
        const existing = acc.get(title);
        if (existing) {
            existing.data.push(photo);
        }
        else {
            acc.set(title, { sortKey, data: [photo] });
        }
        return acc;
    }, new Map());
    return Array.from(groupMap.entries())
        .sort(([, a], [, b]) => a.sortKey.localeCompare(b.sortKey))
        .map(([title, { data: groupData }]) => ({ id: title, title, data: groupData }));
}
function buildPreviewRenderer(previewablePhotos, onThumbClick) {
    function PreviewRenderer({ item }) {
        return (React.createElement("button", { type: "button", className: "building-photos-drawer__thumb-btn", "aria-label": t('preview_photo', { name: item.name }), onClick: () => onThumbClick(item.id), "data-testid": `photo-thumb-${item.id}` }, item.previewUrl ? (React.createElement("img", { src: item.previewUrl, alt: "", className: "building-photos-drawer__thumb" })) : (React.createElement("i", { className: "fa-light fa-image building-photos-drawer__thumb-placeholder", "aria-hidden": "true" }))));
    }
    PreviewRenderer.displayName = `PreviewRenderer(${previewablePhotos.length})`;
    return PreviewRenderer;
}
function DescriptionRenderer({ item }) {
    const { related, caption } = item;
    return (React.createElement("span", { className: "building-photos-drawer__description" },
        caption && React.createElement("span", { className: "building-photos-drawer__caption" }, caption),
        related && (React.createElement("span", { className: "building-photos-drawer__related-tag", "data-testid": `related-tag-${item.id}` }, related.kind === 'inspection'
            ? t('related_inspection', { id: related.id })
            : t('related_work_order', { id: related.id })))));
}
function TypeRenderer({ item }) {
    return React.createElement("span", null, item.typeLabel);
}
function DateTakenRenderer({ item }) {
    return React.createElement("span", null, formatDateTaken(item.dateTaken));
}
function UploadedByRenderer({ item }) {
    const { name, avatarUrl } = item.uploadedBy;
    if (!name)
        return React.createElement("span", null, "\u2014");
    return (React.createElement("span", { className: "building-photos-drawer__uploader", "data-testid": `uploader-${item.id}` },
        avatarUrl ? (React.createElement("img", { src: avatarUrl, alt: "", "aria-hidden": "true", className: "building-photos-drawer__avatar" })) : (React.createElement("span", { className: "building-photos-drawer__avatar-initials", "aria-hidden": "true" }, name.charAt(0).toUpperCase())),
        React.createElement("span", null, name)));
}
const DEFAULT_PHOTO_FILTERS = {
    dateTaken: { start: null, end: null },
    type: null,
    uploadedBy: [],
    search: ''
};
function typeOptions() {
    return [
        { value: 'Inspection', label: t('filter_type_inspection') },
        { value: 'Work Order', label: t('filter_type_work_order') }
    ];
}
// ── Component ─────────────────────────────────────────────────────────────────
export function BuildingPhotosDrawer({ photos, isLoading, onAdd, onClose, open }) {
    const [selected, setSelected] = useState([]);
    const [filters, setFilters] = useState(DEFAULT_PHOTO_FILTERS);
    const [carouselOpen, setCarouselOpen] = useState(false);
    const [carouselIndex, setCarouselIndex] = useState(0);
    const uploaderOptions = useMemo(() => Array.from(new Set(photos.map((p) => p.uploadedBy.name).filter((n) => Boolean(n))))
        .sort()
        .map((name) => ({ value: name, label: name })), [photos]);
    const filteredPhotos = useMemo(() => photos.filter((p) => {
        var _a, _b;
        if (filters.type && p.typeLabel !== filters.type)
            return false;
        if (filters.uploadedBy.length > 0 && !filters.uploadedBy.includes((_a = p.uploadedBy.name) !== null && _a !== void 0 ? _a : ''))
            return false;
        if (filters.dateTaken.start || filters.dateTaken.end) {
            if (!p.dateTaken)
                return false;
            const d = parseDateSafe(p.dateTaken);
            if (!d)
                return false;
            if (filters.dateTaken.start && d < filters.dateTaken.start)
                return false;
            if (filters.dateTaken.end && d > filters.dateTaken.end)
                return false;
        }
        if (filters.search) {
            const q = filters.search.toLowerCase();
            if (!((_b = p.caption) === null || _b === void 0 ? void 0 : _b.toLowerCase().includes(q)) && !p.name.toLowerCase().includes(q))
                return false;
        }
        return true;
    }), [photos, filters]);
    const previewablePhotos = useMemo(() => filteredPhotos.filter((p) => p.previewUrl), [filteredPhotos]);
    const slides = useMemo(() => previewablePhotos.map((p) => ({ src: p.previewUrl, alt: p.name })), [previewablePhotos]);
    const handleThumbClick = useCallback((photoId) => {
        const idx = previewablePhotos.findIndex((p) => p.id === photoId);
        setCarouselIndex(Math.max(0, idx));
        setCarouselOpen(true);
    }, [previewablePhotos]);
    const handleSelectionChange = useCallback((id, checked) => {
        const numId = Number(id);
        setSelected((prev) => (checked ? [...prev, numId] : prev.filter((x) => x !== numId)));
    }, []);
    const handleSelectAll = useCallback((selectAll) => {
        setSelected(selectAll ? filteredPhotos.map((p) => p.id) : []);
    }, [filteredPhotos]);
    const handleAdd = () => {
        photos.filter((p) => selected.includes(p.id)).forEach((p) => onAdd(p));
        setSelected([]);
    };
    const tableData = useMemo(() => groupPhotosByMonth(filteredPhotos), [filteredPhotos]);
    const columns = useMemo(() => [
        {
            dataKey: 'preview',
            label: t('col_photo'),
            width: '80px',
            renderer: buildPreviewRenderer(previewablePhotos, handleThumbClick)
        },
        {
            dataKey: 'description',
            label: t('col_description'),
            renderer: DescriptionRenderer
        },
        {
            dataKey: 'type',
            label: t('col_type'),
            width: '12%',
            renderer: TypeRenderer
        },
        {
            dataKey: 'dateTaken',
            label: t('col_date_taken'),
            width: '12%',
            renderer: DateTakenRenderer
        },
        {
            dataKey: 'uploadedBy',
            label: t('col_uploaded_by'),
            width: '18%',
            renderer: UploadedByRenderer
        }
    ], [previewablePhotos, handleThumbClick]);
    const filterConfigs = useMemo(() => [
        {
            type: 'dateRange',
            field: 'dateTaken',
            label: t('filter_date_taken'),
            value: filters.dateTaken,
            onChange: (v) => setFilters((f) => (Object.assign(Object.assign({}, f), { dateTaken: v })))
        },
        {
            type: 'select',
            field: 'type',
            label: t('filter_type'),
            value: filters.type,
            options: typeOptions(),
            onChange: (v) => setFilters((f) => (Object.assign(Object.assign({}, f), { type: v })))
        },
        {
            type: 'multiSelect',
            field: 'uploadedBy',
            label: t('filter_uploaded_by'),
            value: filters.uploadedBy,
            options: uploaderOptions,
            onChange: (v) => setFilters((f) => (Object.assign(Object.assign({}, f), { uploadedBy: v })))
        }
    ], [filters, uploaderOptions]);
    const filterBar = (React.createElement(FilterBar, { filters: filterConfigs, toggleDisabled: true, showSearch: true, searchValue: filters.search, onSearchChange: (v) => setFilters((f) => (Object.assign(Object.assign({}, f), { search: v }))), onResetFilters: () => setFilters(DEFAULT_PHOTO_FILTERS) }));
    return (React.createElement(React.Fragment, null,
        React.createElement(Brushaway, { className: "building-photos-drawer", controlledOpen: open, onClose: onClose, minWidth: BUILDING_PHOTOS_BRUSHAWAY_MIN_WIDTH, closeOnClickOutside: true },
            React.createElement(BrushawayHeader, { title: t('building_photos') }),
            React.createElement(BrushawayBody, null,
                React.createElement(Table, { data: tableData, columns: columns, isGrouped: true, groupedPresentation: "accordion", variant: "detail", size: TableSize.Full, selectable: true, selectedItems: selected, onSelectionChange: handleSelectionChange, onSelectAll: handleSelectAll, getItemId: (item) => item.id, isLoading: isLoading, filterBar: filterBar, enableCustomScrollbar: true })),
            React.createElement(BrushawayFooter, null,
                React.createElement(Button, { variant: "primary", disabled: selected.length === 0, onClick: handleAdd }, t('add_to_proposal_count', { count: selected.length })),
                onClose && (React.createElement(Button, { variant: "secondary", onClick: onClose }, t('close'))))),
        React.createElement(PhotoCarousel, { open: carouselOpen, index: carouselIndex, slides: slides, onClose: () => setCarouselOpen(false), onIndexChange: setCarouselIndex })));
}
